/**
 * Live Config Store
 *
 * Fetches stream config from GET /api/stream/config and exposes effective cadence
 * (streaming 1s vs processing 10s) for playback, streamingStore, and live map.
 */

import { createSignal } from 'solid-js';
import { apiEndpoints } from '@config/env';
import { getData } from '../utils/global';
import { debug, warn } from '../utils/console';

export type LiveActiveCadence = 'streaming' | 'simulation' | 'processing' | 'idle';

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_PROCESSING_POLL_INTERVAL_MS = 10000;
/** Local dev default: ~3s behind wall clock so boats interpolate continuously through sparse samples. */
const DEFAULT_BUFFER_MS = 3000;
/** Keep playhead behind latest sample so it does not catch the data edge between WS/Influx batches. */
const DEFAULT_DATA_DELAY_MS = 2000;
const DEFAULT_WS_BROADCAST_INTERVAL_MS = 200;
const DEFAULT_PROCESSING_WS_BROADCAST_INTERVAL_MS = 10000;
/** Live map history backfill for streaming/simulation (fast lane vs slower Influx poll). */
const DEFAULT_STREAMING_MAP_REFRESH_INTERVAL_MS = 1000;
/** Live map history backfill for processing mode (capped by processing poll). */
const DEFAULT_TRACK_REFRESH_INTERVAL_MS = 2000;

interface StreamConfigApiData {
  pollIntervalMs?: number;
  processingPollIntervalMs?: number;
  bufferMs?: number;
  dataDelayMs?: number;
  wsBroadcastIntervalMs?: number;
  processingWsBroadcastIntervalMs?: number;
  trackRefreshIntervalMs?: number;
  streamingMapRefreshIntervalMs?: number;
  streamingStarted?: boolean;
  simulationActive?: boolean;
  liveProcessingEnabled?: boolean;
  activeCadence?: LiveActiveCadence;
  effectivePollIntervalMs?: number;
  effectiveWsBroadcastIntervalMs?: number;
  effectiveTrackRefreshIntervalMs?: number;
}

const [pollIntervalMs, setPollIntervalMs] = createSignal(DEFAULT_POLL_INTERVAL_MS);
const [bufferMs, setBufferMs] = createSignal(DEFAULT_BUFFER_MS);
const [dataDelayMs, setDataDelayMs] = createSignal(DEFAULT_DATA_DELAY_MS);
const [wsBroadcastIntervalMs, setWsBroadcastIntervalMs] = createSignal(DEFAULT_WS_BROADCAST_INTERVAL_MS);
const [trackRefreshIntervalMs, setTrackRefreshIntervalMs] = createSignal(DEFAULT_TRACK_REFRESH_INTERVAL_MS);
const [activeCadence, setActiveCadence] = createSignal<LiveActiveCadence>('idle');
const [streamingPollIntervalMs, setStreamingPollIntervalMs] = createSignal(DEFAULT_POLL_INTERVAL_MS);
const [processingPollIntervalMs, setProcessingPollIntervalMs] = createSignal(DEFAULT_PROCESSING_POLL_INTERVAL_MS);
const [streamingWsBroadcastIntervalMs, setStreamingWsBroadcastIntervalMs] = createSignal(
  DEFAULT_WS_BROADCAST_INTERVAL_MS
);
const [processingWsBroadcastIntervalMs, setProcessingWsBroadcastIntervalMs] = createSignal(
  DEFAULT_PROCESSING_WS_BROADCAST_INTERVAL_MS
);
const [isLoaded, setIsLoaded] = createSignal(false);

let fetchPromise: Promise<void> | null = null;
let lastModeKey = '';

function positiveMs(value: unknown, fallback: number): number {
  return typeof value === 'number' && value > 0 ? value : fallback;
}

function modeKeyFromFlags(
  streamingStarted: boolean,
  simulationActive: boolean,
  liveProcessingEnabled: boolean
): string {
  return `${streamingStarted}:${simulationActive}:${liveProcessingEnabled}`;
}

function resolveClientEffectivePoll(data: StreamConfigApiData): number {
  const streamingPoll = positiveMs(data.pollIntervalMs, DEFAULT_POLL_INTERVAL_MS);
  const processingPoll = positiveMs(data.processingPollIntervalMs, DEFAULT_PROCESSING_POLL_INTERVAL_MS);
  if (data.simulationActive || data.streamingStarted) {
    return streamingPoll;
  }
  if (data.liveProcessingEnabled) {
    return processingPoll;
  }
  return streamingPoll;
}

function resolveClientEffectiveWs(data: StreamConfigApiData): number {
  const streamingWs = positiveMs(data.wsBroadcastIntervalMs, DEFAULT_WS_BROADCAST_INTERVAL_MS);
  const processingWs = positiveMs(
    data.processingWsBroadcastIntervalMs,
    DEFAULT_PROCESSING_WS_BROADCAST_INTERVAL_MS
  );
  if (data.simulationActive || data.streamingStarted) {
    return streamingWs;
  }
  if (data.liveProcessingEnabled) {
    return processingWs;
  }
  return streamingWs;
}

function resolveClientEffectiveTrackRefresh(
  activeCadenceVal: LiveActiveCadence,
  effectivePoll: number,
  defaultTrackMs: number,
  streamingMapMs: number
): number {
  if (activeCadenceVal === 'processing') {
    return Math.min(defaultTrackMs, effectivePoll);
  }
  if (activeCadenceVal === 'streaming' || activeCadenceVal === 'simulation') {
    return streamingMapMs;
  }
  return defaultTrackMs;
}

function applyConfigData(data: StreamConfigApiData): void {
  const streamingPoll = positiveMs(data.pollIntervalMs, DEFAULT_POLL_INTERVAL_MS);
  const processingPoll = positiveMs(data.processingPollIntervalMs, DEFAULT_PROCESSING_POLL_INTERVAL_MS);
  const streamingWs = positiveMs(data.wsBroadcastIntervalMs, DEFAULT_WS_BROADCAST_INTERVAL_MS);
  const processingWs = positiveMs(
    data.processingWsBroadcastIntervalMs,
    DEFAULT_PROCESSING_WS_BROADCAST_INTERVAL_MS
  );
  const defaultTrackMs = positiveMs(data.trackRefreshIntervalMs, DEFAULT_TRACK_REFRESH_INTERVAL_MS);
  const streamingMapMs = positiveMs(
    data.streamingMapRefreshIntervalMs,
    DEFAULT_STREAMING_MAP_REFRESH_INTERVAL_MS
  );
  const cadence =
    data.activeCadence === 'streaming' ||
    data.activeCadence === 'simulation' ||
    data.activeCadence === 'processing' ||
    data.activeCadence === 'idle'
      ? data.activeCadence
      : 'idle';

  const effectivePoll = positiveMs(data.effectivePollIntervalMs, resolveClientEffectivePoll(data));
  const effectiveWs = positiveMs(data.effectiveWsBroadcastIntervalMs, resolveClientEffectiveWs(data));
  const effectiveTrack = positiveMs(
    data.effectiveTrackRefreshIntervalMs,
    resolveClientEffectiveTrackRefresh(cadence, effectivePoll, defaultTrackMs, streamingMapMs)
  );

  setStreamingPollIntervalMs(streamingPoll);
  setProcessingPollIntervalMs(processingPoll);
  setStreamingWsBroadcastIntervalMs(streamingWs);
  setProcessingWsBroadcastIntervalMs(processingWs);
  setActiveCadence(cadence);
  setPollIntervalMs(effectivePoll);
  setWsBroadcastIntervalMs(effectiveWs);
  setTrackRefreshIntervalMs(effectiveTrack);
  setBufferMs(positiveMs(data.bufferMs, DEFAULT_BUFFER_MS));
  setDataDelayMs(positiveMs(data.dataDelayMs, DEFAULT_DATA_DELAY_MS));
  setIsLoaded(true);

  lastModeKey = modeKeyFromFlags(
    data.streamingStarted === true,
    data.simulationActive === true,
    data.liveProcessingEnabled === true
  );

  debug('[LiveConfigStore] Config applied', {
    activeCadence: cadence,
    effectivePollIntervalMs: effectivePoll,
    effectiveWsBroadcastIntervalMs: effectiveWs,
    effectiveTrackRefreshIntervalMs: effectiveTrack,
    streamingPollIntervalMs: streamingPoll,
    processingPollIntervalMs: processingPoll,
  });
}

async function loadLiveConfigFromApi(): Promise<void> {
  try {
    const response = await getData(apiEndpoints.stream.config);
    if (response?.success && response?.data) {
      applyConfigData(response.data as StreamConfigApiData);
    } else {
      debug('[LiveConfigStore] Config response missing data, using defaults');
    }
  } catch (err) {
    warn('[LiveConfigStore] Failed to fetch stream config, using defaults', err);
  }
}

/**
 * Fetch stream config from server. Safe to call multiple times; only one request in flight.
 */
export async function fetchLiveConfig(): Promise<void> {
  if (fetchPromise) {
    return fetchPromise;
  }
  fetchPromise = (async () => {
    await loadLiveConfigFromApi();
    fetchPromise = null;
  })();
  return fetchPromise;
}

/**
 * Force a fresh config fetch (e.g. when streaming vs processing mode changes).
 */
export async function refetchLiveConfig(): Promise<void> {
  fetchPromise = null;
  await loadLiveConfigFromApi();
}

/**
 * Refetch when streaming status flags change (streaming vs processing cadence).
 */
export function syncLiveConfigFromStatus(snapshot: {
  streamingStarted: boolean;
  simulationActive: boolean;
  isLiveProcessingActive: boolean;
}): void {
  const key = modeKeyFromFlags(
    snapshot.streamingStarted,
    snapshot.simulationActive,
    snapshot.isLiveProcessingActive
  );
  if (key === lastModeKey) {
    return;
  }
  void refetchLiveConfig();
}

export const liveConfigStore = {
  /** Effective Influx poll interval for current cadence (streaming or processing). */
  pollIntervalMs,
  /** Wall-clock display lag (playhead = now − bufferMs). */
  bufferMs,
  /** Catalog safety margin — playhead ≤ latestData − dataDelayMs. */
  dataDelayMs,
  /** Effective WebSocket broadcast cadence for current cadence. */
  wsBroadcastIntervalMs,
  /** Effective live map track history merge cadence. */
  trackRefreshIntervalMs,
  /** Active cadence from server: streaming | simulation | processing | idle. */
  activeCadence,
  /** Raw streaming poll interval from env (1s default). */
  streamingPollIntervalMs,
  /** Raw processing poll interval from env (10s default). */
  processingPollIntervalMs,
  /** Raw streaming WS broadcast interval (200ms default). */
  streamingWsBroadcastIntervalMs,
  /** Raw processing WS broadcast interval (defaults to processing poll). */
  processingWsBroadcastIntervalMs,
  /** Whether config has been loaded from API. */
  isLoaded,
  fetchLiveConfig,
  refetchLiveConfig,
  syncLiveConfigFromStatus,
};
